<template>
  <Dialog :open="isOpen" @update:open="updateOpen">
    <DialogContent class="sm:max-w-[1000px] h-[800px] flex flex-col">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription> Ask questions or discuss with this bubble </DialogDescription>
      </DialogHeader>

      <div class="flex-1 overflow-hidden">
        <ChatInterface
          :chat-type="ChatType.BUBBLE"
          :identifier="props.bubbleUuid"
          :title="title"
          :is-loading="isLoading"
          :is-typing="isTyping"
          :messages="messages"
          :is-streaming="isStreaming"
          @send="handleSend"
          @clear="handleClear"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ChatType } from '@/types/chatEnums'
import ChatInterface from '@/components/chat/ChatInterface.vue'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { watch, onUnmounted, computed } from 'vue'
import { useChatStore } from '@/stores/useChatStore'
import { generateChatId } from '@/utils/chatUtils'
import { useToast } from '@/components/ui/toast/use-toast'

const props = defineProps<{
  isOpen: boolean
  bubbleUuid: string
  title: string
}>()

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
}>()

const chatStore = useChatStore()
const { toast } = useToast()

const chatId = computed(() =>
  props.bubbleUuid ? generateChatId(ChatType.BUBBLE, props.bubbleUuid) : null
)

watch(
  () => props.isOpen,
  async (isOpen) => {
    log('🎯 Modal open state changed:', { isOpen, bubbleId: props.bubbleUuid })
    if (isOpen && props.bubbleUuid && chatId.value) {
      log('🎯 Modal opened, initializing chat for bubble:', props.bubbleUuid)

      try {
        chatStore.setActiveChat(ChatType.BUBBLE, props.bubbleUuid)
        log('🎯 Set active chat in store')

        await chatStore.getChatHistory(ChatType.BUBBLE, props.bubbleUuid)
        log('🎯 Loaded chat history')

        log('🎯 Chat history loaded:', {
          messageCount: chatStore.activeMessages.length,
          messages: chatStore.activeMessages.map((m) => ({
            role: m.role,
            content: m.content?.substring(0, 20) + '...',
            timestamp: m.timestamp,
          })),
          bubbleId: props.bubbleUuid,
        })
      } catch (error) {
        logError('Failed to load chat history:', error)
        toast({
          title: 'Error',
          description: 'Failed to load chat history',
          variant: 'destructive',
        })
      }
    }
  },
  { immediate: true }
)

const messages = computed(() => chatStore.activeMessages)
const isLoading = computed(() => chatStore.loading)
const isTyping = computed(() => chatStore.isTyping)
const isStreaming = computed(() => chatStore.isStreaming)

const handleSend = async (content: string) => {
  log('🎯 ChatModal handleSend ENTRY:', {
    content,
    bubbleId: props.bubbleUuid,
    isTyping: isTyping.value,
    isStreaming: isStreaming.value,
  })

  try {
    if (!props.bubbleUuid) {
      throw new Error('No bubble ID provided')
    }

    await chatStore.sendMessage(content)

    log('🎯 Message send initiated successfully')
  } catch (error) {
    logError('🎯 Error sending message:', error)
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to send message',
      variant: 'destructive',
    })
  }
}

const handleClear = async () => {
  if (!props.bubbleUuid) return
  try {
    await chatStore.clearHistory(ChatType.BUBBLE, props.bubbleUuid)
    toast({ title: 'Success', description: 'Chat history cleared' })
  } catch (error) {
    logError('Failed to clear chat history:', error)
    toast({
      title: 'Error',
      description: 'Failed to clear chat history',
      variant: 'destructive',
    })
  }
}

const updateOpen = (value: boolean) => {
  emit('update:isOpen', value)
}

onUnmounted(() => {
  if (chatId.value) {
    log('🎯 Cleaning up bubble chat:', chatId.value)
    chatStore.cleanupChat(chatId.value)
  }
})
</script>
